

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import { supabase } from '../../services/supabaseClient';
import { 
    ArrowRight, Loader2, AlertCircle, Instagram, 
    ShieldCheck, ArrowLeft, CheckCircle2, User, BarChart3, LogOut
} from 'lucide-react';
import { Logo } from '../../components/ui/Logo';

// Tipo para os dados pré-carregados do Insta
interface ScrapedProfile {
    username: string;
    profilePicUrl?: string;
    followers: number;
    fullName?: string;
    biography?: string;
}

const LoginPage: React.FC = () => {
  const { signInWithGoogle, session, loading: authLoading, signOut } = useAuth();
  const { createBusiness, business, isLoading: storeLoading } = useStore();
  const navigate = useNavigate();
  
  // Onboarding Steps (apenas visíveis se logado && sem business)
  const [step, setStep] = useState<'USERNAME' | 'ANALYZING' | 'PREVIEW'>('USERNAME');
  
  // Data State
  const [username, setUsername] = useState('');
  const [niche, setNiche] = useState('');
  const [scrapedData, setScrapedData] = useState<ScrapedProfile | null>(null);
  
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // 1. REDIRECT IF SUCCESS
  useEffect(() => {
      if (!authLoading && !storeLoading && session && business) {
          navigate('/admin/dashboard', { replace: true });
      }
  }, [session, business, authLoading, storeLoading, navigate]);

  const handleAnalyzeProfile = async () => {
    if (!username) {
        setErrorMsg("Digite o usuário do Instagram.");
        return;
    }
    
    setErrorMsg(null);
    setStep('ANALYZING');
    
    // Normaliza username
    const cleanUser = username.replace('@', '').trim();
    const formattedUser = `@${cleanUser}`;
    setUsername(formattedUser);

    try {
        const { data, error } = await supabase.functions.invoke('instagram-scraper', {
            body: { username: cleanUser }
        });

        if (error || !data || data.error) {
            console.warn("Scraper falhou ou offline, usando fallback visual.");
            setTimeout(() => {
                setScrapedData({
                    username: formattedUser,
                    followers: 1000, 
                    profilePicUrl: `https://ui-avatars.com/api/?name=${cleanUser}&background=00E676&color=000&size=200`
                });
                setStep('PREVIEW');
            }, 1500);
        } else {
            setScrapedData({
                username: formattedUser,
                followers: data.followers || 0,
                // Prioriza a imagem retornada, se falhar o onError trata
                profilePicUrl: data.profilePic || `https://ui-avatars.com/api/?name=${cleanUser}&background=00E676&color=000`,
                fullName: data.fullName,
                biography: data.biography
            });
            if (data.biography) {
                const inferredNiche = data.biography.includes('mkt') ? 'Marketing' : 
                                      data.biography.includes('fit') ? 'Fitness' : 'Creator';
                setNiche(inferredNiche);
            }
            setStep('PREVIEW');
        }
    } catch (e) {
        setStep('USERNAME');
        setErrorMsg("Não foi possível analisar o perfil. Verifique a conexão.");
    }
  };

  const handleCreateAccount = async () => {
      if (!scrapedData) return;
      setIsCreating(true);
      try {
          await createBusiness({
              username: scrapedData.username,
              niche: niche || 'Creator',
              logoUrl: scrapedData.profilePicUrl // Passando a URL da imagem para salvar
          });
          // O useEffect de redirect vai assumir daqui
      } catch (err: any) {
          console.error("Setup failed:", err);
          setIsCreating(false);
          
          if (err.message?.includes('violates unique constraint')) {
              setErrorMsg(`O perfil ${scrapedData.username} já está cadastrado.`);
              setStep('USERNAME'); 
              setScrapedData(null);
          } else {
              setErrorMsg(err.message || "Erro ao criar conta.");
          }
      }
  };

  const handleGoogleLogin = async () => {
      setIsLoggingIn(true);
      try {
          await signInWithGoogle();
      } catch (err: any) {
          setErrorMsg(err.message);
          setIsLoggingIn(false);
      }
  };

  // --- RENDERS ---

  if (authLoading || storeLoading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-10 border-[15px] border-brand-green">
          <Logo size="xl" className="mb-10" />
          <div className="w-32 h-32 border-[10px] border-brand-green border-t-white rounded-full animate-spin mb-10 shadow-[0_0_30px_#00E676]"></div>
          <p className="text-brand-green font-black uppercase tracking-[0.5em] text-4xl italic text-center animate-pulse">
            CARREGANDO...
          </p>
      </div>
    );
  }

  // Se já tem business, não deveria estar aqui (useEffect redireciona), mas renderiza null para evitar flash
  if (session && business) return null;

  return (
    <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center p-4 md:p-6 relative overflow-hidden selection:bg-brand-green selection:text-black">
      <div className="w-full max-w-4xl bg-black border-[4px] border-brand-green p-8 md:p-12 lg:p-16 shadow-[12px_12px_0px_#00E676] animate-scale-in relative transition-all duration-500">
        
        {/* HEADER */}
        <header className="text-center space-y-6 mb-12">
             <div className="inline-flex items-center gap-3 bg-brand-green text-black px-6 py-2 border-4 border-black font-black uppercase text-xs tracking-widest shadow-bold-sm rotate-[-2deg]">
                 {session ? 'SETUP INICIAL' : 'ACESSO RESTRITO'}
             </div>
             <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase leading-[0.8] italic text-balance mt-6 text-white flex flex-col items-center">
                 <Logo size="lg" className="mb-4" />
                 HEROFROG <br/> 
                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-white">CONNECT.</span>
             </h1>
        </header>

        {errorMsg && (
            <div className="mb-10 bg-red-600 text-white border-[4px] border-black p-6 flex items-center gap-4 font-black uppercase text-xs tracking-wide shadow-bold-sm animate-vibrate">
                <AlertCircle className="w-8 h-8 shrink-0" />
                <span>{errorMsg}</span>
            </div>
        )}

        {/* --- VIEW 1: NOT LOGGED IN -> GOOGLE LOGIN --- */}
        {!session && (
            <div className="space-y-6 animate-slide-up text-center max-w-md mx-auto">
                <p className="text-2xl font-black uppercase italic tracking-tighter text-white">Identifique-se</p>
                
                <div className="py-4">
                    <button
                        onClick={handleGoogleLogin}
                        disabled={isLoggingIn}
                        className="w-full bg-white text-black border-[4px] border-black font-black py-6 text-base uppercase tracking-widest flex items-center justify-center gap-4 shadow-bold hover:translate-y-[-2px] transition-all hover:bg-brand-green"
                    >
                        {isLoggingIn ? <Loader2 className="w-6 h-6 animate-spin" /> : <> <User className="w-6 h-6" /> Entrar com Google</>}
                    </button>
                    <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mt-4">Acesso único e seguro</p>
                </div>
            </div>
        )}

        {/* --- VIEW 2: LOGGED IN (NO BUSINESS) -> ONBOARDING --- */}
        {session && !business && (
            <div className="max-w-lg mx-auto">
                {step === 'USERNAME' && (
                    <div className="space-y-8 animate-slide-up">
                        <div className="space-y-2">
                            <label className="text-sm font-black uppercase tracking-widest text-brand-green ml-1">Qual seu Instagram?</label>
                            <div className="relative group">
                                <Instagram className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-white transition-colors w-6 h-6" />
                                <input 
                                    autoFocus
                                    placeholder="@seu_perfil"
                                    className="w-full bg-[#111] border-[3px] border-[#333] focus:border-brand-green p-6 pl-16 text-2xl md:text-3xl font-black uppercase italic outline-none text-white transition-all placeholder-gray-700"
                                    value={username}
// @ts-ignore
                                    onChange={(e) => setUsername((e.target as HTMLInputElement).value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAnalyzeProfile()}
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleAnalyzeProfile}
                            className="w-full bg-white text-black border-[3px] border-black font-black py-6 text-xl uppercase tracking-tighter italic flex items-center justify-center gap-4 hover:bg-brand-green hover:shadow-[0_0_20px_#00E676] transition-all"
                        >
                            BUSCAR PERFIL <ArrowRight className="w-6 h-6" />
                        </button>
                        
                        <div className="text-center pt-4">
                            <button onClick={signOut} className="text-gray-500 hover:text-red-500 text-xs font-bold uppercase tracking-widest flex items-center gap-2 mx-auto">
                                <LogOut size={12} /> Trocar Conta Google
                            </button>
                        </div>
                    </div>
                )}

                {step === 'ANALYZING' && (
                    <div className="py-12 flex flex-col items-center justify-center space-y-6 animate-fade-in">
                        <div className="relative">
                            <div className="w-24 h-24 border-4 border-[#333] rounded-full animate-spin border-t-brand-green"></div>
                            <Logo size="md" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                        </div>
                        <p className="text-gray-600 font-bold text-xs uppercase tracking-widest">Analisando @{username}...</p>
                    </div>
                )}

                {step === 'PREVIEW' && scrapedData && (
                    <div className="animate-scale-in">
                        <div className="bg-[#111] border border-white/10 rounded-3xl p-8 mb-8 relative overflow-hidden">
                            <div className="flex items-center gap-6 relative z-10">
                                <img 
                                    src={scrapedData.profilePicUrl} 
                                    alt="Profile" 
                                    referrerPolicy="no-referrer"
// @ts-ignore
                                    onError={(e) => {
                                        // Fallback robusto se a imagem do Insta falhar por CORS/403
                                        // FIX: Property 'src' does not exist on type 'HTMLImageElement'. Cast to any.
                                        (e.currentTarget as any).src = `https://ui-avatars.com/api/?name=${scrapedData.username}&background=00E676&color=000&size=200`;
                                    }}
                                    className="w-20 h-20 rounded-full border-4 border-brand-green object-cover bg-black"
                                />
                                <div>
                                    <h3 className="text-2xl font-black italic text-white">{scrapedData.username}</h3>
                                    <p className="text-gray-400 font-bold uppercase text-xs mt-1">
                                        {scrapedData.followers.toLocaleString()} Seguidores
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={handleCreateAccount}
                                disabled={isCreating}
                                className="w-full bg-brand-green text-black border-[3px] border-black font-black py-6 text-xl uppercase tracking-tight flex items-center justify-center gap-3 shadow-bold hover:translate-y-[-2px] transition-all hover:bg-white"
                            >
                                {isCreating ? <Loader2 className="animate-spin" /> : <> <ShieldCheck className="w-6 h-6" /> Confirmar Perfil</>}
                            </button>
                            
                            <button 
                                onClick={() => { setStep('USERNAME'); setScrapedData(null); }}
                                className="w-full py-4 text-gray-500 font-bold uppercase text-xs tracking-widest hover:text-white transition-colors"
                            >
                                Não é este perfil?
                            </button>
                        </div>
                    </div>
                )}
            </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;